import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { Chart, registerables } from "chart.js";
import CastingCsvreader from "./CastingCsvreader";
import DampingRatioCell from "./DampingRatioCell";
import axios from "axios";
import { API_BASE_URL } from './config';
import "./style.css";
import "./FrequencyResponseFunction.css";
import zoomPlugin from 'chartjs-plugin-zoom';
Chart.register(...registerables, zoomPlugin);

const CastingFRF = () => {
  const navigate = useNavigate();
  const onCastingManagement = (e) => {
    e.preventDefault();
    navigate('/casting-management');
  };
  const onCastingCreate = (e) => {
    e.preventDefault();
    navigate('/casting-create');
  };

  //從CastingCsvreader抓取CastingLoading狀態，若為true，禁用軸向切換，出現提示字
  const [CastingLoading, setCastingLoading] = useState(localStorage.getItem("CastingLoading") === "true");
  //每0.5秒更新一次狀態
  useEffect(() => {
    const interval = setInterval(() => {
      const currentLoading = localStorage.getItem("CastingLoading") === "true";
      setCastingLoading(currentLoading);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const [csvData, setcsvData] = useState({
    Amp_peak_freq_data: Array(6).fill(''),
    Amp_peak_data: Array(6).fill(''),
    Damping_ratio_data: Array(6).fill(null),
    Error_freq: Array(6).fill(''),
    Error_amp: Array(6).fill(''),
    ch_name: "",
    filename: "",
    freq: [],
    magdb: [],
    magn: [],
    phase: [],
    time: ""
  });

  const [updata, setupData] = useState([]);
  //儲存篩選後的資料，取代原本的updata
  const [newUpdata, setUpdata] = useState([]);
  const [coverData, setcoverData] = useState([]);
  const [lineLabels, setLineLabels] = useState([]);

  const onCastingManagementSave = async (e) => {
    e.preventDefault();

    // 如果檔案或資料未正確匯入，則儲存失敗
    if (!csvData.filename) {
      alert('儲存失敗，請確認所有軸向的資料皆已正確匯入');
      console.error('儲存失敗，請確認所有軸向的資料皆已正確匯入');
      return;
    }

    // 取用 updata
    const savedUpdata = localStorage.getItem("updata");

    // 淺拷貝 castingData 並命名為 combinedData
    const combinedData = { 
       ...(localStorage.getItem("castingData") ? JSON.parse(localStorage.getItem("castingData")) : {}),
       username: localStorage.getItem("username")
    };

    // 直接將解析後的資料儲存到 combinedData 中
    combinedData.updata = savedUpdata ? JSON.parse(savedUpdata) : updata;

    // 邏輯判斷，檢查資料是否有效
    const isDataInvalid = (data) => {
      return (
        !data ||
        !Array.isArray(data.Amp_peak_data) ||
        data.Amp_peak_data.length === 0 ||
        !Array.isArray(data.Amp_peak_freq_data) ||
        data.Amp_peak_freq_data.length === 0
      );
    };

    // 如果有任何資料不完整，則顯示錯誤訊息
    if (isDataInvalid(combinedData.updata)) {
      alert('儲存失敗，請確認所有軸向的資料皆已正確匯入');
      console.error('儲存失敗，請確認所有軸向的資料皆已正確匯入');
      return;
    }

    try {
      // 發送 POST 請求儲存資料
      const response = await axios.post(`${API_BASE_URL}/upload/castingData`, combinedData);
      console.log('儲存成功', response.data);

      // 清除 localStorage 中的資料
      localStorage.removeItem("castingData");
      localStorage.removeItem("csvData");
      localStorage.removeItem("coverData");
      localStorage.removeItem("lineLabels");
      localStorage.removeItem("updata");


      // 導向到鑄件管理頁面
      navigate('/casting-management');
    } catch (error) {
      alert('儲存失敗，請確認所有軸向的資料皆已正確匯入');
      console.error('儲存失敗，請確認所有軸向的資料皆已正確匯入', error);
    }
  };

  const [selectedMachines, setSelectedMachines] = useState([]);
  const [machineList, setMachineList] = useState([]);

  const handleConfirmSelection = () => {
    const newChartData = selectedMachines.map((machineId) => {
      const machine = machineList.find((m) => m._id === machineId);
      if (!machine) return null;
      const r = Math.floor(Math.random() * 255);
      const g = Math.floor(Math.random() * 255);
      const b = Math.floor(Math.random() * 255);
      return {
        label: machine.updata.chartData.ch_name,
        borderColor: `rgb(${r}, ${g}, ${b})`,
        backgroundColor: `rgba(${r}, ${g}, ${b}, 0.2)`,
        data: Array.isArray(machine.updata.chartData.magn) ? machine.updata.chartData.magn : [machine.updata.chartData.magn],
        fill: true,
        tension: 0.4,
        labels: machine.updata.chartData.freq,
      };
    }).filter(item => item !== null);
    setcoverData(newChartData);
    setLineLabels(newChartData[0]?.labels.slice(9) || [])
  };

  useEffect(() => {
    if (selectedMachines.length > 0) {
      handleConfirmSelection();
    }
  });

  const lineChartRef = useRef(null);

  const createLineChart = (chartRef, datasets) => {
    const chartInstance = new Chart(lineChartRef.current, {
      type: "line",
      data: {
        labels: chartRef,
        datasets: datasets
      },
      options: {
        layout: {
          padding: { right: -60 } // 圖例位置調整
        },
        scales: {
          y: {
            ticks: { beginAtZero: true },
            title: { display: true, text: 'Amplitude (μm/N)', font: { size: 14 }, padding: 15 }
          },
          x: {
            type: 'linear',
            min: 1000, max: 5000,
            title: { display: true, text: 'Frequency (Hz)', font: { size: 14 }, padding: 15 },
            ticks: {
              beginAtZero: true,
              stepSize: 100,
              callback: function (value) {
                return Math.round(value);
              }
            }
          }
        },
        responsive: true,
        maintainAspectRatio: false,
        fill: true,
        elements: {
          point: { radius: 0, hitRadius: 10 },
          line: { tension: 0.4 }
        },
        plugins: {
          legend: {
            position: 'bottom',
            display: true,
            labels: {
              usePointStyle: true,
              pointStyle: 'rect',
              font: { size: 13 } // 折線圖圖例尺寸
            }
          },
          tooltip: {
            enabled: true,
            callbacks: {
              label: function (context) {
                const label = context.dataset.label || '';
                const xValue = context.label;
                const yValue = context.raw.toFixed(4);
                return `${label}   ( ${xValue} , ${yValue} )`;
              },
              title: function () {
                return '';
              }
            }
          },
          datalabels: { display: false },
          zoom: {
            pan: {
              enabled: false, // 禁用拖曳平移，避免拖動時圖表一起動
            },
            zoom: {
              wheel: { enabled: false }, // 禁用滾輪縮放
              pinch: { enabled: false }, // 禁用觸控縮放
              mode: 'x',
              drag: {
                enabled: true,
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                threshold: 2,
                delay: 50 // 增加延遲，讓放大不會即時觸發
              }
            },
            onZoom: ({ chart }) => {
              let xScale = chart.scales.x;
              let min = Math.max(xScale.min, 1000);
              let max = Math.min(xScale.max, 5000);
              chart.options.scales.x.min = min;
              chart.options.scales.x.max = max;
              chart.options.scales.x.ticks.stepSize = 100;  // 重新設定間距
              chart.update();
            }
          }
        }
      }
    });
    lineChartRef.current.chartInstance = chartInstance;
    return chartInstance;
  };

  function getRandomColor() {
    const r = Math.floor(Math.random() * 255);
    const g = Math.floor(Math.random() * 255);
    const b = Math.floor(Math.random() * 255);
    return {
      borderColor: `rgb(${r}, ${g}, ${b})`,
      backgroundColor: `rgba(${r}, ${g}, ${b}, 0.2)`
    };
  }

  const resetZoom = () => {
    if (lineChartRef.current && lineChartRef.current.chartInstance) {
      lineChartRef.current.chartInstance.resetZoom();
    }
  };

  useEffect(() => {
    let lineChart;
    if (lineChartRef.current)
      lineChart?.destroy();
    lineChart = createLineChart(lineLabels, coverData);
    return () => {
      lineChart?.destroy();
    };
  }, [coverData]);

  const location = useLocation();
  const [freqDifferences, setFreqDifferences] = useState([]);
  const [ampDifferences, setAmpDifferences] = useState([]);
  const [castingData, setCastingData] = useState(location.state?.castingData || {});
  const [baselineData, setBaselineData] = useState([]);

  useEffect(() => {
    console.log('接收到的基本資料', castingData);
  }, [castingData]);

  useEffect(() => {
    const fetchCastingData = async () => {
      const username = localStorage.getItem('username') || '未知使用者';
      try {
        const response = await axios.get(`${API_BASE_URL}/getdata/formData`, {
          headers: { 'x-username': username }
        });
        const fetchedData = response.data;
        const uniqueCompanyModelPairs = new Set();
        const firstEntries = [];

        fetchedData.forEach(machine => {
          const key = `${machine.company}-${machine.machineModel}`;
          if (!uniqueCompanyModelPairs.has(key)) {
            uniqueCompanyModelPairs.add(key);
            firstEntries.push(machine);
          }
        });

        const matchingEntry = firstEntries.find(entry =>
          entry?.company === castingData?.company &&
          entry?.machineModel === castingData?.machineModel
        );


        if (matchingEntry) {
          const freqDiffs = [];
          const ampDiffs = [];

          csvData.Amp_peak_freq_data.forEach((freq, i) => {
            //匯入值
            const amp = csvData.Amp_peak_data[i];
            //基準，判斷頻率差距是否在正負一內，回傳第一個符合條件的index，若找不到則index為 -1
            const matchIndex = matchingEntry.updata.Amp_peak_freq_data.findIndex((baseFreq) =>
              Math.abs(freq - baseFreq) <= 1
            );

            if (matchIndex !== -1) {
              const baseFreq = matchingEntry.updata.Amp_peak_freq_data[matchIndex];
              const baseAmp = matchingEntry.updata.Amp_peak_data[matchIndex];

              const freqDiff = Math.abs((freq - baseFreq) / baseFreq);
              const ampDiff = Math.abs((amp - baseAmp) / baseAmp);

              /*console.log(`Freq 比較：第 ${i} 筆資料 (${freq}) 與 基準第 ${matchIndex} 筆 (${baseFreq})`);
              console.log(`Amp 比較：第 ${i} 筆資料 (${amp}) 與 基準第 ${matchIndex} 筆 (${baseAmp})，頻率相符於 ${baseFreq}`);*/

              freqDiffs.push((freqDiff * 100).toFixed(1));
              ampDiffs.push((ampDiff * 100).toFixed(1));
            } else {
              /*console.log(`Freq 比較：第 ${i} 筆資料 (${freq}) 無符合的基準值`);
              console.log(`Amp 比較：第 ${i} 筆資料 (${amp}) 無符合頻率的基準值`);*/

              freqDiffs.push(null);
              ampDiffs.push(null);
            }
          });
          setFreqDifferences(freqDiffs);
          setAmpDifferences(ampDiffs);

          // freqDiffs & ampDiffs 計算後
          const validIndexes = freqDiffs
            .map((_, index) => index)
            .filter(index => freqDiffs[index] !== null && ampDiffs[index] !== null);

          // 新增：只保留有比對成功的資料
          const filteredAmpPeakFreq = validIndexes.map(i => csvData.Amp_peak_freq_data[i]);
          const filteredAmpPeakAmp = validIndexes.map(i => csvData.Amp_peak_data[i]);
          const filteredDampingRatios = validIndexes.map(i => csvData.Damping_ratio_data?.[i] ?? null);
          const filteredFreqDiffs = validIndexes.map(i => freqDiffs[i]);
          const filteredAmpDiffs = validIndexes.map(i => ampDiffs[i]);

          // 鑄件篩選後的 updata
          const newUpdata = {
            ...updata,
            Amp_peak_freq_data: filteredAmpPeakFreq,
            Amp_peak_data: filteredAmpPeakAmp,
            Damping_ratio_data: filteredDampingRatios,
            Amp_peak_freq_diff: filteredFreqDiffs,
            Amp_peak_amp_diff: filteredAmpDiffs,
            Amp_peak_freq_size: filteredAmpPeakFreq.length,
            Amp_peak_size: filteredAmpPeakAmp.length
          };

          setUpdata(newUpdata);
          localStorage.setItem("updata", JSON.stringify(newUpdata));
        }

        setBaselineData(firstEntries);
      } catch (error) {
        console.error("Error fetching casting data:", error);
      }
    };
    fetchCastingData();
  }, [castingData, csvData]);

  useEffect(() => {
    const stateCastingData = location.state?.castingData;
    if (stateCastingData) {
      localStorage.setItem("castingData", JSON.stringify(stateCastingData));
      setCastingData(stateCastingData);
    } else {
      const savedAssembData = localStorage.getItem("castingData");
      if (savedAssembData) {
        setCastingData(JSON.parse(savedAssembData));
      }
    }
    const savedCsvData = localStorage.getItem("csvData");
    if (savedCsvData) {
      setcsvData(JSON.parse(savedCsvData));
    }
    const savedCoverData = localStorage.getItem("coverData");
    if (savedCoverData) {
      setcoverData(JSON.parse(savedCoverData));
    }
    const savedLineLabels = localStorage.getItem("lineLabels");
    if (savedLineLabels) {
      setLineLabels(JSON.parse(savedLineLabels));
    }
    const savedUpdata = localStorage.getItem("updata");
    if (savedUpdata) {
      setupData(JSON.parse(savedUpdata));
    }
  }, [location.state]);

  const handleFileload = (e) => {
    const file = e.target.files[0];
    if (file) {
      CastingCsvreader(file, (data) => {
        console.log('CastingCsvreader data:', data);
        const newCsvData = {
          Amp_peak_freq_data: data.Amp_peak_freq_data || Array(6).fill(''),
          Amp_peak_data: data.Amp_peak_data || Array(6).fill(''),
          Damping_ratio_data: data.Damping_ratio_data || Array(6).fill(null),
          Error_freq: data.Error_freq || Array(6).fill(''),
          Error_amp: data.Error_amp || Array(6).fill(''),
          ch_name: data.chartData?.ch_name || '',
          filename: data.chartData?.filename || '',
          freq: data.chartData?.freq || [],
          magdb: data.chartData?.magdb || [],
          magn: data.chartData?.magn || [],
          phase: data.chartData?.phase || [],
          time: data.chartData?.time || []
        };
        setcsvData(newCsvData);
        localStorage.setItem("csvData", JSON.stringify(newCsvData));
        if (data.chartData && data.chartData.freq) {
          console.log('Valid chartData:', data.chartData);
          setupData(data);
          //儲存剛從csv轉換出來的資料
          localStorage.setItem("updata", JSON.stringify(data));
          const matchingEntry = baselineData.find(entry =>
            entry?.company === castingData.company &&
            entry?.machineModel === castingData.machineModel
          );

          if (matchingEntry) {
            const newCoverData = [
              {
                label: `${matchingEntry.updata.chartData.ch_name} (基準值)`,
                data: matchingEntry.updata.chartData.magn.slice(9),
                borderColor: getRandomColor().borderColor,
                backgroundColor: getRandomColor().backgroundColor
              },
              {
                label: data.chartData.ch_name,
                data: data.chartData.magn.slice(9),
                borderColor: getRandomColor().borderColor,
                backgroundColor: getRandomColor().backgroundColor
              }
            ];
            setcoverData(newCoverData);
            localStorage.setItem("coverData", JSON.stringify(newCoverData));
          } else {
            const newCoverData = [{
              label: data.chartData.ch_name,
              data: data.chartData.magn.slice(9),
              borderColor: getRandomColor().borderColor,
              backgroundColor: getRandomColor().backgroundColor
            }];
            setcoverData(newCoverData);
            localStorage.setItem("coverData", JSON.stringify(newCoverData));
            setBaselineData(prev => ([
              ...prev,
              {
                machineModel: castingData.machineModel,
                updata: data
              }
            ]));
          }
          const newLineLabels = data.chartData.freq.slice(9);
          setLineLabels(newLineLabels);
          localStorage.setItem("lineLabels", JSON.stringify(newLineLabels));
        } else {
          console.error('Invalid chartData:', data);
        }
      });
    }
  };

  const popover = (
    <Popover id="popover-basic">
      <Popover.Body>
        數值不在標準範圍內，為危險值！
      </Popover.Body>
    </Popover>
  );

  //在沒有有效資料時顯示 '計算中…'，但如果 5 秒內都沒有新資料，則顯示'頻率偏差過大，請匯入其他筆資料再試一次'
  const [showErrorMsg, setShowErrorMsg] = useState(false);
  useEffect(() => {
    //查看每個 index，如果 index 對應的 freqDifferences 和 ampDifferences 都不是 null，就是有效的
    const isEmpty = ((freqDifferences?.length > 0 ? freqDifferences : csvData.Amp_peak_freq_data) || [])
      .map((_, index) => index)
      .filter(index => freqDifferences?.[index] !== null && ampDifferences?.[index] !== null)
      //沒有有效的資料
      .length === 0;
    
    console.log("fkjioejfrioej",isEmpty)
    let timer;

    if (csvData.filename && isEmpty) {
      setShowErrorMsg(false); // 先顯示'計算中...'
      timer = setTimeout(() => {
        setShowErrorMsg(true); // 5秒後還是沒值，顯示'頻率偏差過大...'
      }, 50000000000);
    } else {
      setShowErrorMsg(false); // 如果有資料就不會有這個訊息
    }
    return () => clearTimeout(timer);
  }, [csvData.filename, freqDifferences, ampDifferences]);

  return (
    <div className="d-flex" style={{ width: '81.7%', margin: '0 auto' }}>
      <div className="container-fluid">
        <div style={{ height: '35px', width: '100%' }}></div>

        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#" onClick={onCastingManagement} style={{ color: '#B0B7C3', fontWeight: 'bold', fontSize: '15px' }}>鑄件資料庫</a>
            </li>
            <li
              className="breadcrumb-item"
              aria-current="page"
              style={{ color: '#5B82B1', fontWeight: 'bold', fontSize: '15px' }}>新增頻率響應函數
            </li>
          </ol>
        </nav>

        <div className="d-flex justify-content-end gap-3">

          <button
            className="reset-btn bi bi-arrow-clockwise"
            onClick={resetZoom}
          >
          </button>
          <label
            htmlFor="fileUpload"
            className="btn1"
            style={{
              height: '35px',
              width: '75px',
              display: 'inline-block',
              textAlign: 'center',
              lineHeight: '35px',
              backgroundColor: '#007bff',
              color: '#fff',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            上傳檔案
          </label>
          <input
            type="file"
            id="fileUpload"
            style={{ display: "none" }}
            accept=".csv"
            onChange={handleFileload}
          />

        </div>

        {CastingLoading && <div className="loading-center">匯入中，請稍候...</div>}

        <div style={{ height: '35px', width: '100%' }}></div>

        <div className="tab-content" id="nav-tabContent">
          <div className="tab-pane show active p-0 my-4">

            <div style={{ width: '99%', height: '500px' }}>
              <canvas ref={lineChartRef}></canvas>
            </div>

            <div className="p-4">
              <div className="table-responsive">
                <table className="table table-striped table-bordered text-center frf-peak-table">
                  <colgroup>
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '17.6%' }} />
                    <col style={{ width: '17.6%' }} />
                    <col style={{ width: '17.6%' }} />
                    <col style={{ width: '17.6%' }} />
                    <col style={{ width: '17.6%' }} />
                  </colgroup>
                  <thead className="custom-thead ">
                    <tr>
                      <th>資料ID</th>
                      <th>頻率</th>
                      <th>振幅</th>
                      <th>阻尼比</th>
                      <th>線差(f)</th>
                      <th>幅差(a)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvData.filename ? (() => {
                      const validIndexes = ((freqDifferences?.length > 0 ? freqDifferences : csvData.Amp_peak_freq_data) || [])
                        .map((_, index) => index)
                        .filter(index => freqDifferences?.[index] !== null && ampDifferences?.[index] !== null);

                      if (validIndexes.length === 0) {
                        return (
                          <tr>
                            <td colSpan="6" style={{ textAlign: 'center', color: '#999' }}>
                              {showErrorMsg
                                ? '頻率偏差過大，請匯入其他筆資料再試一次'
                                : '計算中...'}
                            </td>
                          </tr>
                        );
                      }

                      return validIndexes.map(index => {
                        const freq = csvData.Amp_peak_freq_data?.[index] || 'NaN';
                        const amp = freq === 'NaN' ? 'NaN' : (csvData.Amp_peak_data?.[index] || 'NaN');

                        return (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td style={{ color: freq === 'NaN' ? '#CCCCCC' : '' }}>{freq}</td>
                            <td style={{ color: amp === 'NaN' ? '#CCCCCC' : '' }}>{amp}</td>
                            <DampingRatioCell value={csvData.Damping_ratio_data?.[index]} />

                            <OverlayTrigger
                              trigger={['hover', 'focus']}
                              placement="top"
                              overlay={freqDifferences?.[index] < 0 || freqDifferences?.[index] > 3 ? popover : <></>}
                            >
                              <td
                                style={{
                                  color: freq !== 'NaN' && (freqDifferences?.[index] < 0 || freqDifferences?.[index] > 3)
                                    ? '#EC4643'
                                    : (freq === 'NaN' ? '#CCCCCC' : '')
                                }}
                              >
                                {freq !== 'NaN'
                                  ? (isFinite(freqDifferences?.[index]) ? `${freqDifferences[index]}%` : '')
                                  : 'NaN'}
                              </td>
                            </OverlayTrigger>

                            <OverlayTrigger
                              trigger={['hover', 'focus']}
                              placement="top"
                              overlay={
                                (() => {
                                  if (amp !== 'NaN' && freq !== 'NaN') {
                                    const ampValue = parseFloat(amp);
                                    const freqDiff = ampDifferences?.[index];
                                    if ((ampValue >= 0.05 && freqDiff > 10) || (ampValue < 0.05 && freqDiff > 30)) {
                                      return popover;
                                    }
                                  }
                                  return <></>;
                                })()
                              }
                            >
                              <td
                                style={{
                                  color: (() => {
                                    if (amp !== 'NaN' && freq !== 'NaN') {
                                      const ampValue = parseFloat(amp);
                                      const freqDiff = ampDifferences?.[index];
                                      if ((ampValue >= 0.05 && freqDiff > 10) || (ampValue < 0.05 && freqDiff > 30)) {
                                        return '#EC4643';
                                      }
                                    }
                                    return '';
                                  })()
                                }}
                              >
                                {freq !== 'NaN'
                                  ? (isFinite(ampDifferences?.[index]) ? `${ampDifferences[index]}%` : '')
                                  : 'NaN'}
                              </td>
                            </OverlayTrigger>
                          </tr>
                        );
                      });
                    })() : (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', color: '#999' }}>
                          尚未上傳檔案
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2">
          <div className="d-flex justify-content-end" style={{ paddingRight: '10px' }}>
            <button
              className="btn-cancel"
              style={{ height: '35px', width: '75px' }}
              onClick={onCastingCreate}
            >
              上一步
            </button>
          </div>
          <div className="d-flex justify-content-end" style={{ paddingRight: '20px' }}>
            <button
              className="btn-confirm btn-primary"
              style={{ height: '35px', width: '75px' }}
              onClick={onCastingManagementSave}
            >
              儲存資料
            </button>
          </div>
        </div>

        <div style={{ height: '60px', width: '100%' }}></div>

      </div>
    </div >
  );
};

export default CastingFRF;
